import type { Tool } from "@/types/tool";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import ToolCardHeader from "./tool/ToolCardHeader";
import ToolCardImage from "./tool/ToolCardImage";
import ToolCardActions from "./tool/ToolCardActions";
import ToolCardContainer from "./tool/card/ToolCardContainer";
import ToolCardDescription from "./tool/card/ToolCardDescription";
import ToolCardAddress from "./tool/card/ToolCardAddress";
import ToolCardTags from "./tool/card/ToolCardTags";
import ToolCardPricing from "./tool/card/ToolCardPricing";

interface ToolCardProps {
  tool: Tool;
  onBusinessNameClick?: (lat: number | undefined, lng: number | undefined) => void;
  onTagClick?: (tag: string) => void;
}

const ToolCard = ({ tool, onBusinessNameClick, onTagClick }: ToolCardProps) => {
  const [favicon, setFavicon] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [isOwnerState, setIsOwnerState] = useState(false);
  const [address, setAddress] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchBusinessImage();
    fetchBusinessAddress();
    checkOwnership();
  }, [tool.visitUrl, tool.logo, tool.id]);

  const checkOwnership = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setIsOwnerState(false);
      return;
    }

    const { data: profile } = await supabase
      .from('professional_profiles')
      .select('user_id')
      .eq('id', tool.id)
      .maybeSingle();

    setIsOwnerState(profile?.user_id === session.user.id);
  };

  const fetchBusinessAddress = async () => {
    if (!tool.id) return;

    try {
      const { data: profile } = await supabase
        .from('professional_profiles')
        .select('address, city, state')
        .eq('id', tool.id)
        .maybeSingle();

      if (profile) {
        const fullAddress = [profile.address, profile.city, profile.state]
          .filter(Boolean)
          .join(', ');
        setAddress(fullAddress);
      }
    } catch (error) {
      console.error('Error fetching business address:', error);
    }
  };

  const fetchBusinessImage = async () => {
    if (!tool.id) return;

    try {
      const { data: profile } = await supabase
        .from('professional_profiles')
        .select('business_image_url')
        .eq('id', tool.id)
        .maybeSingle();

      if (profile?.business_image_url) {
        setFavicon(profile.business_image_url);
        return;
      }

      setFavicon(tool.logo);
    } catch (error) {
      console.error('Error in business image fetch:', error);
      setFavicon(tool.logo);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      setUploading(true);
      const fileExt = file.name.split('.').pop();
      const filePath = `${tool.id}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('professional-photos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('professional-photos')
        .getPublicUrl(filePath);

      await updateBusinessImage(publicUrl);
      setFavicon(publicUrl);

      toast({
        title: "Success",
        description: "Business image uploaded successfully",
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: "Failed to upload image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const updateBusinessImage = async (imageUrl: string) => {
    try {
      const { error } = await supabase
        .from('professional_profiles')
        .update({ business_image_url: imageUrl })
        .eq('id', tool.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating business image:', error);
    }
  };

  const handleLocationClick = () => {
    if (onBusinessNameClick && tool.latitude && tool.longitude) {
      onBusinessNameClick(tool.latitude, tool.longitude);
    }
  };

  return (
    <ToolCardContainer>
      <div className="space-y-4">
        <div className="flex items-start gap-4 sm:gap-6">
          <div onClick={handleLocationClick} className="cursor-pointer">
            <ToolCardImage
              tool={tool}
              favicon={favicon}
              uploading={uploading}
              isOwner={isOwnerState}
              onImageUpload={handleImageUpload}
            />
          </div>
          <div className="flex-1 min-w-0">
            <ToolCardHeader
              tool={tool}
              favicon={favicon}
              uploading={uploading}
              isOwner={isOwnerState}
              onImageUpload={handleImageUpload}
              onBusinessNameClick={handleLocationClick}
            />
            <ToolCardDescription description={tool.description} />
          </div>
        </div>

        <div className="space-y-3 pt-2 border-t border-gray-100">
          <ToolCardAddress address={address} />
          <div className="flex items-center justify-between gap-2">
            <ToolCardTags tags={tool.tags} onTagClick={onTagClick} />
            <ToolCardPricing pricing={tool.pricing} />
          </div>
          <ToolCardActions tool={tool} />
        </div>
      </div>
    </ToolCardContainer>
  );
};

export default ToolCard;
